import { useParams, Link } from 'react-router-dom';
import { getFiliereBySlug } from '@/data/filieres';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FaGraduationCap, FaClock, FaClipboardCheck, FaBriefcase, FaBook, FaArrowLeft, FaArrowRight, FaStar } from 'react-icons/fa';

export default function FiliereDetail() {
  const { slug } = useParams();
  const filiere = getFiliereBySlug(slug);

  if (!filiere) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Filière non trouvée</h1>
          <Link to="/filieres">
            <Button className="bg-brand-primary hover:bg-brand-primary-dark text-white">
              <FaArrowLeft className="mr-2" /> Retour aux filières
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-[300px] sm:h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${filiere.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary/95 to-brand-primary/80"></div>
        </div>
        <div className="relative z-10 h-full flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/filieres" 
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <FaArrowLeft className="mr-2" /> Retour aux filières
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              filiere.type === 'licence' 
                ? 'bg-green-500 text-white' 
                : 'bg-purple-500 text-white'
            }`}>
              {filiere.type === 'licence' ? 'LICENCE' : 'MASTER'}
            </span>
            <span className="px-3 py-1 bg-white/20 rounded-full text-white text-sm">
              {filiere.code}
            </span>
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
            {filiere.nom}
          </h1>
          <p className="text-lg text-white/80">
            {filiere.diplome}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 -mt-16 mb-12 relative z-20">
          <Card className="p-6 bg-white shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-brand-primary/10 rounded-lg">
                <FaClock className="text-2xl text-brand-primary" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Durée</p>
                <p className="font-bold text-brand-secondary">{filiere.duree}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FaGraduationCap className="text-2xl text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-bold text-brand-secondary uppercase">{filiere.type}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <FaBook className="text-2xl text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Code</p>
                <p className="font-bold text-brand-secondary">{filiere.code}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white shadow-lg sm:col-span-2 lg:col-span-1">
            <a href="https://scolarite-cefat.net/preinscription">
              <Button className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3">
                S'inscrire <FaArrowRight className="ml-2" />
              </Button>
            </a>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Objectifs */}
            <Card className="p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-secondary mb-6 flex items-center gap-3">
                <FaStar className="text-brand-primary" />
                Objectifs de la Formation
              </h2>
              <ul className="space-y-3">
                {filiere.objectifs?.map((obj, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">{obj}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Compétences (if available) */}
            {filiere.competences && filiere.competences.length > 0 && (
              <Card className="p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-brand-secondary mb-6 flex items-center gap-3">
                  <FaClipboardCheck className="text-green-600" />
                  Compétences Visées
                </h2>
                <ul className="space-y-3">
                  {filiere.competences.map((comp, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">{comp}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Spécialités (if available) */}
            {filiere.specialites && filiere.specialites.length > 0 && (
              <Card className="p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-brand-secondary mb-6">
                  Spécialités
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {filiere.specialites.map((spec, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg border-l-4 border-brand-primary">
                      <p className="font-medium text-gray-800">{spec}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Options (if available) */}
            {filiere.options && filiere.options.length > 0 && (
              <Card className="p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-brand-secondary mb-6">
                  Options Disponibles
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {filiere.options.map((opt, index) => (
                    <div key={index} className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                      <p className="font-medium text-gray-800">{opt}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Débouchés */}
            <Card className="p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-secondary mb-6 flex items-center gap-3">
                <FaBriefcase className="text-orange-500" />
                Débouchés Professionnels
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {filiere.debouches?.map((deb, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="w-8 h-8 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary font-bold text-sm">
                      {index + 1}
                    </span>
                    <span className="text-gray-700 text-sm">{deb}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Conditions d'accès */}
            <Card className="p-6 bg-gradient-to-br from-brand-secondary to-blue-900 text-white">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <FaClipboardCheck />
                Conditions d'Accès
              </h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {filiere.conditions}
              </p>
            </Card>

            {/* CTA */}
            <Card className="p-6 border-2 border-brand-primary">
              <h3 className="text-lg font-bold text-brand-secondary mb-4">
                Intéressé par cette filière ?
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                Rejoignez l'Université CEFAT International et bénéficiez d'une formation d'excellence.
              </p>
              <a href="https://scolarite-cefat.net/preinscription">
                <Button className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white">
                  Préinscription en ligne
                </Button>
              </a>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Ou contactez notre conseiller d'orientation
                </p>
                <a 
                  href="tel:0758140795" 
                  className="block text-center text-brand-primary font-bold mt-2 hover:underline"
                >
                  07 58 14 07 95
                </a>
              </div>
            </Card>

            {/* Contact */}
            <Card className="p-6 bg-gray-50">
              <h3 className="text-lg font-bold text-brand-secondary mb-4">
                Besoin d'informations ?
              </h3>
              <div className="space-y-3 text-sm">
                <p className="text-gray-600">
                  <strong>Email:</strong><br />
                  <a href="mailto:contact@universitecefat.ci" className="text-brand-primary hover:underline">
                    contact@universitecefat.ci
                  </a>
                </p>
                <p className="text-gray-600">
                  <strong>Téléphones:</strong><br />
                  <a href="tel:0712651719" className="text-brand-primary hover:underline">07 12 65 17 19</a><br />
                  <a href="tel:0100959518" className="text-brand-primary hover:underline">01 00 95 95 18</a>
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
